import { ReactNode, useEffect, useMemo, useState } from "react";
import { LanderV2MarketingProps, LanderV2StepProps } from "../../../Campaigns/Schemas/LanderV2/types";
import { IQuote } from "../../../../models/QuoteData";
import { getQuoteGroups, getSortedQuotes, isFixedTermQuote, quoteKey } from "./quoteGroups";
import { formatCurrency, getProviderLogo } from "../../../../modules/common/quotes/quoteUtils";
import { SurveyJSForm } from "../../../../modules/surveyjs_forms/SurveyJSForm";
import { lv2Theme } from "../../../../modules/surveyjs_forms/surveyTheme";
import { variantBAssets, variantBProviders } from "../../../Utils/variantBAssets";
import { TrustpilotReviewsCarousel } from "../TrustpilotReviewsCarousel";
import { useUserAuth } from "../../../../context/AuthContext";
import { usePortal } from "../../../../context/PortalContext";
import { IClientData } from "../../../../models/ClientData";
import { IAnonClientData } from "../../../../models/AnonClientData";
import {
  AppointmentBookingPayload,
  CalendlyBookingDetails,
  CalendlyCustomQuestionConfig,
  CalendlyCustomWidget,
  CalendlyDetailsForm,
} from "../CalendlyCustomWidget";
import { LanderV2LoadingOverlay } from "../LanderV2LoadingOverlay";
import { QuoteExplainerDrawer, QuoteExplainerType } from "../shared/QuoteExplainerDrawer";
import { QuoteAdviceActions } from "../shared/QuoteAdviceActions";
import { AdhocBookingPanel, buildInitialBookingDetails } from "../shared/AdhocBookingPanel";
import { trackBehaviourEvent } from "../../../../helpers/userBehaviourTrackingHelpers";
import { ComplianceNote } from "../shared/ComplianceNote";
import { QuoteBoostBand } from "../shared/QuoteBoostBand";
import { PartnerAssociationBlock } from "../../../../modules/common/menus/PartnerAssociationBlock";

export const tickGreenRibbedSrc = "/media/misc/tick_green_ribbed.png";
export const fscsRibbedSrc = "/media/logos/fscs_protected_small_ribbed.png";
const fscsWhiteSrc = "/media/misc/fscs-white.png";
const bestDealBadgeSrc = "/media/public-acquisition/variant-b/best-deal-badge.png";

export interface Variant1ProcessStep {
  title: string;
  copy: string;
  image: string;
}

const processSteps: readonly Variant1ProcessStep[] = [
  {
    title: "1. Fill in the basics",
    copy: "Tell us a little about you and your pension in a quick, secure form.",
    image: variantBAssets.processFeedback,
  },
  {
    title: "2. We find the best deals",
    copy: "We find the highest-paying products that match your specific goals.",
    image: variantBAssets.processProfit,
  },
  {
    title: "3. Get FREE expert advice",
    copy: "Connect with qualified pensions advisor to ensure which product is right for you.",
    image: variantBAssets.processSchool,
  },
  {
    title: "4. Sit back and relax!",
    copy: "Congratulations. You have officially maximised your pension and guaranteed yourself the best deal.",
    image: variantBAssets.processRelax,
  },
];

export const v1CallNumber = "0808 281 2558";
export const v1CallHref = "tel:08082812558";
const v1AppointmentType = "portal-general-appointment";
export const v1AppointmentCustomQuestions: CalendlyCustomQuestionConfig[] = [
  {
    question: "Please confirm your phone number",
    position: 0,
    required: true,
    label: "Phone",
    inputType: "tel",
    autoComplete: "tel",
    bookingDetailField: "phone",
  },
];

const heroChecks = [
  {
    key: "best-deals",
    content: (
      <>
        Find the <strong>best deals</strong> on the open market in minutes
      </>
    ),
  },
  {
    key: "free-advice",
    content: (
      <>
        Get <strong>FREE</strong> expert guidance on the right products
      </>
    ),
  },
  {
    key: "boost",
    content: (
      <>
        Boost your pension up to <strong>47%</strong>
      </>
    ),
  },
  {
    key: "free",
    content: (
      <>
        <strong>100% FREE</strong> - it doesn't cost you a penny!
      </>
    ),
  },
];

const providerLogoOverrides: Record<string, string> = {
  aviva: variantBAssets.providerAviva,
  "canada life": variantBAssets.providerCanadaLife,
  just: variantBAssets.providerJust,
  "just retirement": variantBAssets.providerJust,
  "legal & general": variantBAssets.providerLegalGeneral,
  "legal and general": variantBAssets.providerLegalGeneral,
  lv: variantBAssets.providerLv,
  "lv=": variantBAssets.providerLv,
  prudential: variantBAssets.providerPrudential,
  "scottish widows": variantBAssets.providerScottishWidows,
  "standard life": variantBAssets.providerStandardLife,
};

function QuoteEntrySurvey({
  id,
  entrySchemaPage,
  surveyProps,
  initialData,
  onQuoteSubmit,
  onFirstInteraction,
}: Pick<LanderV2MarketingProps, "entrySchemaPage" | "surveyProps" | "initialData" | "onQuoteSubmit" | "onFirstInteraction"> & { id: string }) {
  return (
    <SurveyJSForm
      surveyFormId={id}
      pageSchemas={[entrySchemaPage]}
      initialData={initialData}
      clearDraftOnComplete={false}
      surveyProps={surveyProps}
      surveyTheme={lv2Theme}
      wrapperFormCssClass="lv2-survey pa-v1-entry-survey"
      surveyErrorTextColor="#ffffff"
      keepFormVisibleOnComplete
      suppressLoader
      onFirstInteraction={onFirstInteraction}
      onComplete={(data) => onQuoteSubmit(data ?? {})}
    />
  );
}

export function RatingProof({ compact = false }: { compact?: boolean }) {
  return (
    <div className={compact ? "pa-v1-rating pa-v1-rating-compact" : "pa-v1-rating"}>
      <span>Rated <strong>Excellent</strong></span>
      <span className="pa-v1-rating-stars" aria-label="5 star rating">
        <i>★</i>
        <i>★</i>
        <i>★</i>
        <i>★</i>
        <i>★</i>
      </span>
    </div>
  );
}

export function scrollToEntryForm() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

export function Variant1ProviderSection() {
  return (
    <section
      className="pa-vb-section pa-vb-provider-section pa-v1-provider-section"
      aria-labelledby="variant-v1-providers"
    >
      <h2 id="variant-v1-providers" className="ux2-text-heading">
        We compare quotes from every major UK provider
      </h2>
      <div className="pa-vb-provider-strip pa-v1-provider-strip">
        {[...variantBProviders, ...variantBProviders].map(([name, src], index) => (
          <img key={`${name}-${index}`} src={src} alt={index >= variantBProviders.length ? "" : name} />
        ))}
      </div>
    </section>
  );
}

export function Variant1ProcessSection({
  onGetStarted = scrollToEntryForm,
  steps = processSteps,
}: {
  onGetStarted?: () => void;
  /** The four "how it works" steps; defaults to VLM3's. */
  steps?: readonly Variant1ProcessStep[];
}) {
  return (
    <section className="pa-vb-process" aria-labelledby="variant-v1-process">
      <h2 id="variant-v1-process" className="ux2-text-heading">
        Getting the most from your pension has never been easier
      </h2>
      <div className="pa-vb-process-grid">
        {steps.map((step) => (
          <div className="pa-vb-process-step" key={step.title}>
            <img src={step.image} alt="" aria-hidden="true" />
            <div className="pa-vb-process-step-copy">
              <h3>{step.title}</h3>
              <p className="ux2-text-body">{step.copy}</p>
            </div>
          </div>
        ))}
      </div>
      <button
        className="pa-vb-button ux2-text-button"
        type="button"
        onClick={onGetStarted}
      >
        GET STARTED
      </button>
    </section>
  );
}

export function Variant1ReviewsSection() {
  return (
    <section
      className="pa-vb-section pa-vb-reviews"
      aria-labelledby="variant-v1-reviews"
    >
      <h2 id="variant-v1-reviews" className="ux2-text-heading">
        We have helped over <span className="pa-v1-blue-text">1,000</span> customers get the most from their pension
      </h2>
      <TrustpilotReviewsCarousel />
    </section>
  );
}

export function Variant1SafeHandsRow({
  ctaHeading = "Ready to see what your pension will payout?",
  ctaCopy = (
    <>
      Get quotes as a little as <strong>2 minutes</strong>
    </>
  ),
  onGetStarted = scrollToEntryForm,
}: {
  ctaHeading?: string;
  ctaCopy?: ReactNode;
  onGetStarted?: () => void;
}) {
  return (
    <section className="pa-vb-card-row pa-v1-card-row" aria-label="Safety and quote start">
      <div className="pa-vb-safe-card">
        <img src={variantBAssets.fscsProtected} alt="FSCS protected" />
        <div>
          <h2 className="ux2-text-safe-heading">You are in safe hands!</h2>
          <p className="ux2-text-body-lg">
            We only partner with FCA-regulated advisors, meaning your pension
            switch is fully backed by the <strong>FSCS protection scheme.</strong>
          </p>
        </div>
      </div>
      <div className="pa-vb-quote-card pa-v1-inline-cta">
        <img src={variantBAssets.onlineSchool} alt="" aria-hidden="true" />
        <div>
          <h2 className="ux2-text-heading">{ctaHeading}</h2>
          <p className="ux2-text-body-lg">{ctaCopy}</p>
          <button className="pa-vb-button ux2-text-button" type="button" onClick={onGetStarted}>
            GET STARTED
          </button>
        </div>
      </div>
    </section>
  );
}

/** Without `onBookAppointment` only the phone number is offered. */
export function Variant1TalkSection({ onBookAppointment }: { onBookAppointment?: () => void }) {
  return (
    <section className="pa-vb-talk" aria-labelledby="variant-v1-talk">
      <img
        src={variantBAssets.talkIllustration}
        alt="Appointment booking illustration"
      />
      <div>
        <h2 id="variant-v1-talk" className="ux2-text-heading">Prefer to talk?</h2>
        <p className="ux2-text-body-lg">
          Speak directly to a specialist pension advisor for <strong>free</strong>, friendly
          guidance on maximising your retirement income with no strings
          attached.
        </p>
        <p className="pa-vb-phone ux2-text-phone">
          <span>Call free on</span>
          <a href={v1CallHref}><strong>{v1CallNumber}</strong></a>
          {onBookAppointment ? (
            <>
              <span>or</span>
              <button type="button" className="ux2-text-button" onClick={onBookAppointment}>
                BOOK AN APPOINTMENT
                <i className="bi bi-calendar3" aria-hidden="true"></i>
              </button>
            </>
          ) : null}
        </p>
      </div>
    </section>
  );
}

export function LanderV2MarketingVariant1(props: LanderV2MarketingProps) {

  return (
    <article className="pa-v1">
      <header className="pa-v1-top">
        <img src={variantBAssets.penseLogo} alt="Pense" />
        <PartnerAssociationBlock className="pa-v1-associated" />
      </header>

      <section className="pa-v1-hero" aria-labelledby="pa-v1-title">
        <div className="pa-v1-hero-copy">
          <h1 id="pa-v1-title">{props.resolvedHeading}</h1>
          <ul className="pa-v1-checks">
            {heroChecks.map((check) => (
              <li key={check.key}>
                <span className="pa-v1-check-icon" aria-hidden="true">
                  <img src={tickGreenRibbedSrc} alt="" />
                  <i className="bi bi-check-lg"></i>
                </span>
                <span>{check.content}</span>
              </li>
            ))}
          </ul>
          <RatingProof />
        </div>

        <aside className="pa-v1-cta-card" aria-label="Get annuity quotes">
          <img className="pa-v1-fscs-seal" src={fscsRibbedSrc} alt="FSCS protected" />
          <h2>{props.resolvedSubheading}</h2>
          <QuoteEntrySurvey
            id="annuitycalculator-v1-quote-entry"
            {...props}
          />
        </aside>
      </section>

      <Variant1ProviderSection />
      <Variant1ProcessSection />
      <Variant1ReviewsSection />
      <Variant1SafeHandsRow />
      <Variant1TalkSection onBookAppointment={props.onBookAppointment} />
      <ComplianceNote />
    </article>
  );
}

export function getQuoteLogo(providerName?: string) {
  if (!providerName) return null;
  return providerLogoOverrides[providerName.toLowerCase().trim()] ?? getProviderLogo(providerName);
}

function getQuoteProductDetail(quote: IQuote) {
  if (!isFixedTermQuote(quote)) {
    return "Single Life, Enhanced, Level";
  }

  const termYears = Number(quote.termYears ?? 5);
  const termLabel = `${termYears} year${termYears === 1 ? "" : "s"}`;
  return `Single Life, Fixed Term, ${termLabel}`;
}

const vlm3LoaderBackground = "#ffffff";
const vlm1LoaderBackground = "#ebf4ff";

export function Variant1EntryLoadingOverlay() {
  return (
    <LanderV2LoadingOverlay
      title="Sit tight!"
      subtitle="Checking all major UK suppliers"
      backgroundColor={vlm3LoaderBackground}
    />
  );
}

export function Variant1QuoteLoadingOverlay() {
  return (
    <LanderV2LoadingOverlay
      title="Nearly there"
      subtitle="We're just crunching the numbers"
      backgroundColor={vlm3LoaderBackground}
    />
  );
}

export function Variant1QuoteBlueLoadingOverlay() {
  return (
    <LanderV2LoadingOverlay
      title="Nearly there"
      subtitle="We're just crunching the numbers"
      backgroundColor={vlm1LoaderBackground}
    />
  );
}

function NoQuotesPanel({ onBook }: { onBook: () => void }) {
  return (
    <section className="pa-v1-loading">
      <h1>We couldn't find quotes for these details</h1>
      <p>Our specialists can still find the right option for you.</p>
      <button type="button" className="pa-v1-button" onClick={onBook}>BOOK AN APPOINTMENT</button>
    </section>
  );
}

function QuoteRow({
  quote,
  isBest,
  onMoreInfo,
}: {
  quote: IQuote;
  isBest?: boolean;
  onMoreInfo: () => void;
}) {
  const logo = getQuoteLogo(quote.providerName);
  const productDetail = getQuoteProductDetail(quote);
  return (
    <article className="pa-v1-quote-row">
      <div className="pa-v1-quote-provider">
        {logo ? <img src={logo} alt={quote.providerName} /> : <strong>{quote.providerName}</strong>}
      </div>
      <strong className="pa-v1-quote-amount">{formatCurrency(quote.annuityAmountPA)}</strong>
      <span className="pa-v1-quote-detail">
        <strong>{quote.providerName}</strong>
        {productDetail && <span>{productDetail}</span>}
      </span>
      {isBest && <img className="pa-v1-best-badge" src={bestDealBadgeSrc} alt="Best deal" />}
      <button type="button" className="ux2-text-button" onClick={onMoreInfo}>MORE INFO</button>
    </article>
  );
}

function AdviceCard({ onBook }: { onBook: () => void }) {
  return (
    <aside className="pa-v1-advice-card">
      <h2 className="ux2-text-heading">Not sure which is right for you?</h2>
      <div className="pa-v1-advisor-faces" aria-hidden="true">
        <img src={variantBAssets.advisorSmall1} alt="" />
        <img src={variantBAssets.advisorSmall2} alt="" />
        <img src={variantBAssets.advisorSmall3} alt="" />
      </div>
      <p className="ux2-text-body-lg">Our expert Pension Advisors will help on the right product, at the very best rates</p>
      <QuoteAdviceActions
        actionsClassName="pa-v1-advice-actions"
        buttonClassName="pa-v1-button pa-v1-button-magenta ux2-text-button"
        callHref={v1CallHref}
        callNumber={v1CallNumber}
        className="pa-v1-phone-callout"
        onBook={onBook}
      />
    </aside>
  );
}

function QuoteDetailBookingCta({ onBook }: { onBook: () => void }) {
  return (
    <div className="pa-v1-detail-booking-cta">
      <button type="button" className="pa-v1-button ux2-text-button" onClick={onBook}>
        BOOK AN APPOINTMENT
      </button>
      <span>Or call <strong>FREE</strong> on</span>
      <a href={v1CallHref}>
        <i className="bi bi-telephone" aria-hidden="true"></i>
        {v1CallNumber}
      </a>
    </div>
  );
}

export function Variant1QuoteListStep({ lead, quotes, onSelectQuote, goToStep }: LanderV2StepProps) {
  const [infoDrawerType, setInfoDrawerType] = useState<QuoteExplainerType | null>(null);
  const groups = useMemo(() => getQuoteGroups(quotes), [quotes]);
  const firstName = lead.firstName?.trim() ?? "";

  if (quotes.length === 0) {
    return <NoQuotesPanel onBook={() => goToStep("booking")} />;
  }

  return (
    <section className="pa-v1-results">
      <h1>Here are your quotes{firstName ? `, ${firstName}` : ""}</h1>
      <div className="pa-v1-results-grid">
        <div className="pa-v1-results-list">
          {groups.map((group) => (
            <section className="pa-v1-quote-group" key={group.key}>
              <header>
                <h2 className="ux2-text-heading">{group.title}</h2>
                <button
                  type="button"
                  className="lv2-info-link"
                  onClick={() => setInfoDrawerType(group.key === "fixed" ? "fixed" : "lifetime")}
                >
                  What is a {group.key === "fixed" ? "fixed term" : "lifetime"} annuity?
                </button>
              </header>
              <div className="pa-v1-quote-table">
                {group.quotes.map((quote, index) => (
                  <QuoteRow
                    key={quoteKey(quote, index)}
                    quote={quote}
                    isBest={index === 0}
                    onMoreInfo={() => {
                      onSelectQuote(quote);
                      goToStep("quote-detail");
                    }}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="pa-v1-results-sidebar">
          <AdviceCard onBook={() => goToStep("booking")} />
          <div className="pa-v1-review-pullout lv2-quotes-review-card">
            <h3 className="ux2-text-heading">We've helped over <strong>1,000</strong> customers get the most from their pension</h3>
            <TrustpilotReviewsCarousel mode="single" reviewerAvatarPosition="top" />
          </div>
        </div>
      </div>
      <QuoteBoostBand
        callHref={v1CallHref}
        callNumber={v1CallNumber}
        onBook={() => goToStep("booking")}
      />
      {infoDrawerType && <QuoteExplainerDrawer type={infoDrawerType} onClose={() => setInfoDrawerType(null)} />}
    </section>
  );
}

function getBestQuote(quotes: IQuote[]) {
  return getSortedQuotes(quotes)[0] ?? null;
}

function getQuoteExpiryTime(quote?: IQuote | null) {
  if (quote?.createdDate) {
    const created = new Date(quote.createdDate).getTime();
    if (!Number.isNaN(created)) return created + 30 * 86400000;
  }

  if (quote?.expiryDate) {
    const expiry = new Date(quote.expiryDate).getTime();
    if (!Number.isNaN(expiry)) return expiry;
  }

  return Date.now() + 30 * 86400000;
}

function getExpiryParts(quote?: IQuote | null, now = Date.now()) {
  const expiry = getQuoteExpiryTime(quote);
  const remaining = Math.max(0, expiry - now);
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);

  return {
    days: String(days).padStart(2, "0"),
    hours: String(hours).padStart(2, "0"),
    mins: String(mins).padStart(2, "0"),
    secs: String(secs).padStart(2, "0"),
  };
}

export function Variant1QuoteDetailStep({
  quotes,
  selectedQuote,
  onSelectQuote,
  goToStep,
}: LanderV2StepProps) {
  const quote = selectedQuote ?? getBestQuote(quotes);
  const [now, setNow] = useState(() => Date.now());
  const expiry = getExpiryParts(quote, now);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedQuote && quote) onSelectQuote(quote);
  }, [onSelectQuote, quote, selectedQuote]);

  if (!quote) {
    return (
      <section className="pa-v1-loading">
        <h1>Your Quote</h1>
        <p>No quote is available yet.</p>
        <button type="button" className="pa-v1-button ux2-text-button" onClick={() => goToStep("quotes")}>Back to quotes</button>
      </section>
    );
  }

  const logo = getQuoteLogo(quote.providerName);

  return (
    <section className="pa-v1-detail">
      <h1>Your Quote</h1>
      <article className="pa-v1-detail-card">
        <img className="pa-v1-best-badge pa-v1-detail-badge" src={bestDealBadgeSrc} alt="Best deal" />
        <div className="pa-v1-detail-main">
          <div className="pa-v1-detail-logo">
            {logo ? <img src={logo} alt={quote.providerName} /> : <strong>{quote.providerName}</strong>}
          </div>
          <div>
            <strong className="pa-v1-detail-amount">{formatCurrency(quote.annuityAmountPA)}</strong>
            <h2>{quote.providerName}</h2>
            <p>{getQuoteProductDetail(quote)}</p>
            <ul>
              <li>
                <span className="pa-v1-detail-tick" aria-hidden="true">
                  <img src={tickGreenRibbedSrc} alt="" />
                  <i className="bi bi-check-lg"></i>
                </span>
                {isFixedTermQuote(quote)
                  ? `Guaranteed income for ${Number(quote.termYears ?? 5)} year${Number(quote.termYears ?? 5) === 1 ? "" : "s"}`
                  : "Guaranteed income for the rest of your life"}
              </li>
              <li>
                <span className="pa-v1-detail-tick" aria-hidden="true">
                  <img src={tickGreenRibbedSrc} alt="" />
                  <i className="bi bi-check-lg"></i>
                </span>
                Rates secured from the whole UK annuity market
              </li>
            </ul>
            <div className="pa-v1-expiry">
              <span>THIS QUOTE EXPIRES IN</span>
              <div>
                <strong>{expiry.days}<small>Days</small></strong>
                <strong>{expiry.hours}<small>Hours</small></strong>
                <strong>{expiry.mins}<small>Mins</small></strong>
                <strong>{expiry.secs}<small>Secs</small></strong>
              </div>
            </div>
            <button type="button" className="pa-v1-button ux2-text-button" onClick={() => goToStep("booking")}>
              <span className="pa-v1-button-faces" aria-hidden="true">
                <img src={variantBAssets.advisorSmall1} alt="" />
                <img src={variantBAssets.advisorSmall2} alt="" />
                <img src={variantBAssets.advisorSmall3} alt="" />
              </span>
              DISCUSS WITH AN ADVISOR
              <i className="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
            <div className="pa-v1-detail-proof">
              <img src={fscsWhiteSrc} alt="FSCS protected" />
              <RatingProof compact />
            </div>
          </div>
        </div>
      </article>

      <section className="pa-v1-next-steps">
        <h2 className="ux2-text-heading">What happens next?</h2>
        <div>
          <article>
            <img src={variantBAssets.processFeedback} alt="" />
            <p><strong>1.</strong> Book your FREE appointment with a specialist Pension Advisor</p>
          </article>
          <article>
            <img src={variantBAssets.processProfit} alt="" />
            <p><strong>2.</strong> Our advisors check this is the right product for you, and look for ways to boost your quotes even further</p>
          </article>
          <article>
            <img src={variantBAssets.processSchool} alt="" />
            <p><strong>3.</strong> Sit back and relax! You got the best deal for your retirement!</p>
          </article>
        </div>
        <QuoteDetailBookingCta onBook={() => goToStep("booking")} />
      </section>
    </section>
  );
}

function getStringValue(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

type BookingDetails = CalendlyBookingDetails;

type BookingStepProps = LanderV2StepProps & {
  appointmentType?: string;
  // Which IAnnuity date field gets stamped on successful booking - lets the same booking UI
  // serve both the general-appointment and drawdown-appointment funnels while keeping the
  // milestone/task naming distinct on the backend (see ClientController.InsertCalendlyTask).
  annuityDateField?: "bookGeneralAppointmentDate" | "bookGeneralDrawdownAppointmentDate";
  /** Replaces the built-in contact fields with this SurveyJS form. */
  detailsForm?: CalendlyDetailsForm;
  /** Pins the confirm button to the bottom of the screen while it is below the fold. */
  stickyConfirmButton?: boolean;
  /** Disables the details inputs and the calendar while the booking is being confirmed. */
  lockInputsWhileBooking?: boolean;
  /** Shown under the booking card until the slot is booked. */
  disclaimer?: ReactNode;
  /**
   * Runs after Calendly has booked the slot and before the client/update, e.g. a lander's full lead submission.
   * Its result, when given, is the client data the booking is saved onto; null means it saved the booking itself
   * (e.g. on the lead only) and there is no client/update.
   */
  beforeClientUpdate?: (
    payload: AppointmentBookingPayload,
    details: BookingDetails,
    formData?: Record<string, any>
  ) => Promise<IClientData | null | void>;
  /** Replaces the default client/update save after Calendly has booked the slot. */
  onPostBookingCrmUpdate?: (
    payload: AppointmentBookingPayload,
    details: BookingDetails,
    formData?: Record<string, any>
  ) => Promise<void>;
};

export function BookingStepBase({
  lead,
  goToStep,
  appointmentType = v1AppointmentType,
  annuityDateField = "bookGeneralAppointmentDate",
  detailsForm,
  stickyConfirmButton,
  lockInputsWhileBooking,
  disclaimer,
  beforeClientUpdate,
  onPostBookingCrmUpdate,
}: BookingStepProps) {
  const { user } = useUserAuth();
  const { anonymousClientData, clientData, apiUpdateClientData, setClientData, setAnonymousClientData } = usePortal();
  const firstName = getStringValue(lead.firstName);
  const [bookingDetails, setBookingDetails] = useState<BookingDetails>(() => buildInitialBookingDetails(lead));
  const [isBooked, setIsBooked] = useState(false);

  function handleBooked() {
    setIsBooked(true);
  }

  async function handlePostBookingCrmUpdate(payload: any, details: BookingDetails, formData?: Record<string, any>) {
    // Calendly has booked the slot by now; a failed CRM save below is reported as an error, not as a lost booking.
    trackBehaviourEvent("Appointment Booked");
    if (!payload?.calendlyEventJson) {
      trackBehaviourEvent("Lander Error", { stage: "booking" });
      throw new Error("Calendly booking details were missing.");
    }

    const preparedClientData = await beforeClientUpdate?.(payload, details, formData);
    if (preparedClientData === null) return;

    // client/update writes the client record, so start from it; the draft (minus its lead ids) is only a fallback
    // for when the record isn't loaded, e.g. after a refresh.
    const { id: _leadId, transferToID: _transferToID, ...draftClient } = lead;
    const currentClientData = (preparedClientData ??
      (anonymousClientData as IClientData | undefined) ??
      clientData ??
      draftClient) as IClientData;
    // A details form's other answers (address, retirement date) go with the booking; never its lead/session ids.
    const {
      id: _formId,
      transferToID: _formTransferToID,
      tk: _formToken,
      sessionId: _formSessionId,
      ...formAnswers
    } = (formData ?? {}) as IClientData;
    const bookedAt = new Date().toISOString();

    const updatedClientData: IClientData = {
      ...currentClientData,
      ...formAnswers,
      title: details.title,
      firstName: details.firstName,
      lastName: details.lastName,
      email: details.email,
      primaryPhone: details.phone,
      calendlyEventJSON: payload.calendlyEventJson,
      annuity: {
        ...(currentClientData.annuity ?? {}),
        ...(formAnswers.annuity ?? {}),
        [annuityDateField]: bookedAt,
      },
    };

    if (user) {
      setClientData?.(updatedClientData);
    } else {
      const currentAnonData = anonymousClientData as IAnonClientData | undefined;
      setAnonymousClientData?.({
        ...(currentAnonData ?? {}),
        firstName: details.firstName,
        lastName: details.lastName,
        email: details.email,
        primaryPhone: details.phone,
        booking: {
          ...(currentAnonData?.booking ?? {}),
          [annuityDateField]: bookedAt,
        },
      });
    }

    const responseCode = await apiUpdateClientData?.(updatedClientData);
    if (responseCode !== 200) {
      trackBehaviourEvent("Lander Error", { stage: "booking" });
      throw new Error("Client booking update failed.");
    }
  }

  return (
    <section className="pa-v1-booking">
      {!isBooked ? <h1>Let's find a time that works for you{firstName ? `, ${firstName}` : ""}</h1> : null}
      <CalendlyCustomWidget
        appointmentType={appointmentType}
        bookingDetails={bookingDetails}
        onBookingDetailsChange={setBookingDetails}
        onBooked={handleBooked}
        onBookedStateChange={setIsBooked}
        onPostBookingCrmUpdate={onPostBookingCrmUpdate ?? handlePostBookingCrmUpdate}
        onBackToQuotes={() => goToStep("quotes")}
        customQuestionConfigs={v1AppointmentCustomQuestions}
        enableSmsReminderOptIn
        showAsFeaturedOn={false}
        detailsForm={detailsForm}
        stickyConfirmButton={stickyConfirmButton}
        lockInputsWhileBooking={lockInputsWhileBooking}
      />
      {!isBooked ? (
        <>
          {disclaimer ? <div className="pa-v1-booking-disclaimer">{disclaimer}</div> : null}
          <section className="pa-v1-booking-call-now" aria-labelledby="pa-v1-booking-call-now">
            <img src={variantBAssets.talkIllustration} alt="" aria-hidden="true" />
            <div>
              <h2 id="pa-v1-booking-call-now">Need to talk now?</h2>
              <span>Call free on</span>
              <a href={v1CallHref}>{v1CallNumber}</a>
            </div>
          </section>
          <section className="pa-v1-booking-reviews lv2-quotes-review-card" aria-labelledby="pa-v1-booking-reviews">
            <h3 id="pa-v1-booking-reviews" className="ux2-text-heading">We've helped over <strong>1,000</strong> customers get the most from their pension</h3>
            <TrustpilotReviewsCarousel />
          </section>
        </>
      ) : null}
    </section>
  );
}

export function Variant1BookingStep(props: LanderV2StepProps) {
  return <BookingStepBase {...props} />;
}

export function Variant1AdhocBookingStep({ lead, goHome, saveLead }: LanderV2StepProps) {
  const initialDetails = useMemo(() => buildInitialBookingDetails(lead), [lead]);

  async function saveBooking(contact: Partial<IClientData>, booking: Partial<IClientData>) {
    // Calendly has booked the slot; saveLead reports a failed CRM save as "Lander Error".
    trackBehaviourEvent("Adhoc Appointment Booked");
    return saveLead(contact, booking);
  }

  return (
    <AdhocBookingPanel
      initialDetails={initialDetails}
      saveBooking={saveBooking}
      onConfirmationCtaClick={goHome}
    />
  );
}
